import React, { useEffect, useRef, useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { useApiServices } from '../../services/api-endpoints/useApiServices';
import ImageUpload from '../image-upload';
import ImageViewer from '../image-viewer/ImageViewer';
import { toast } from 'react-toastify';
import { IsEmptyGuid } from '../../helper/Formatters';

// ─── Custom Dropdown ────────────────────────────────────────────────────────
const StatusDropdown = ({ selected, statuses, current, onChange }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
            {/* Trigger */}
            <button
                onClick={() => setOpen(o => !o)}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: 7,
                    padding: '4px 10px 4px 8px',
                    background: selected ? `${selected.color}18` : '#f3f4f6',
                    border: `1.5px solid ${selected ? selected.color : '#e5e7eb'}`,
                    borderRadius: 20, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                    color: selected?.color || '#374151',
                    transition: 'all 0.15s',
                }}
            >
                <span style={{
                    width: 10, height: 10, borderRadius: '50%',
                    background: selected?.color || '#9ca3af', flexShrink: 0,
                }} />
                {selected?.labelTh || 'สถานะ'}
                <i className="fa fa-chevron-down" style={{ fontSize: 10, opacity: 0.7, marginLeft: 2 }} />
            </button>

            {/* Menu */}
            {open && (
                <div style={{
                    position: 'absolute', top: 'calc(100% + 6px)', left: 0,
                    background: '#fff', border: '1px solid #e5e7eb',
                    borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    minWidth: 200, zIndex: 1060, overflow: 'hidden',
                }}>
                    <div style={{ padding: '6px 0' }}>
                        {statuses.map((s) => {
                            const isPast = s.transactionStatusKey < current;
                            const isCurrent = s.transactionStatusKey === current;
                            return (
                                <button
                                    key={s.transactionStatusKey}
                                    disabled={s.transactionStatusKey <= current}
                                    onClick={() => { setOpen(false); onChange(s); }}
                                    style={{
                                        width: '100%', display: 'flex', alignItems: 'center',
                                        gap: 10, padding: '8px 14px', background: 'none', border: 'none',
                                        textAlign: 'left', cursor: isPast || isCurrent ? 'not-allowed' : 'pointer',
                                        opacity: isPast ? 0.4 : 1,
                                        fontSize: 13,
                                    }}
                                    onMouseEnter={(e) => { if (!isPast && !isCurrent) e.currentTarget.style.background = '#f9fafb'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                                >
                                    <span style={{
                                        width: 10, height: 10, borderRadius: '50%',
                                        background: s.color || '#9ca3af', flexShrink: 0,
                                    }} />
                                    <span style={{ flex: 1, color: isCurrent ? s.color : '#1f2937', fontWeight: isCurrent ? 600 : 400 }}>
                                        {s.transactionStatusKey === 10 ? 'ยืนยันค่าส่ง' : s.labelTh}
                                    </span>
                                    {isCurrent && (
                                        <span style={{ fontSize: 10, background: `${s.color}20`, color: s.color, borderRadius: 10, padding: '1px 6px', fontWeight: 600 }}>
                                            ปัจจุบัน
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Status Badge (inline) ──────────────────────────────────────────────────
const StatusBadge = ({ status, size = 13 }) => (
    <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '3px 10px 3px 8px',
        background: status ? `${status.color}18` : '#f3f4f6',
        border: `1px solid ${status ? status.color : '#e5e7eb'}`,
        borderRadius: 20, fontSize: size, fontWeight: 600,
        color: status?.color || '#374151',
    }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: status?.color || '#9ca3af' }} />
        {status?.labelTh || '-'}
    </span>
);

// ─── Main Component ──────────────────────────────────────────────────────────
const TransactionStatusSelect = ({ transationId, current, onSuccess, onCanceled, forceChange }) => {
    const api = useApiServices();
    const [statuses, setStatuses] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showModalUpdate, setShowModalUpdate] = useState(false);
    const [newStatus, setNewStatus] = useState(null);
    const [file, setFile] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [paymentDetails, setPaymentDetails] = useState({ description: '', shippingPrice: '' });

    const resetForm = () => {
        setFile(null);
        setErrorMessage('');
        setPaymentDetails({ description: '', shippingPrice: '' });
    };

    const handleClose = () => {
        onCanceled?.();
        setShowModal(false);
        resetForm();
    };

    const handleCloseUpdate = () => {
        onCanceled?.();
        setShowModalUpdate(false);
        setNewStatus(null);
        resetForm();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (IsEmptyGuid(transationId) || !file?.id) return;
        setSubmitting(true);
        const { isSuccess } = await api.ConfirmTransactionAsync(transationId, { fileUploadId: file.id });
        setSubmitting(false);
        if (isSuccess) {
            toast.success('ยืนยันการชำระเงินแล้ว');
            onSuccess?.();
        }
        handleClose();
    };

    const handleSubmitStatus = async (e) => {
        e.preventDefault();
        if (IsEmptyGuid(transationId)) return;
        if (newStatus?.isRequiredReason && !paymentDetails.description.trim()) {
            setErrorMessage('กรุณากรอกหมายเหตุ');
            return;
        }
        setSubmitting(true);
        setErrorMessage('');
        const { isSuccess } = await api.UpdateTransactionStatusAsync(transationId, {
            statusKey: newStatus.transactionStatusKey,
            reason: paymentDetails.description,
            shippingPrice: paymentDetails.shippingPrice || null,
        });
        setSubmitting(false);
        if (isSuccess) {
            toast.success('อัพเดทสถานะแล้ว');
            onSuccess?.();
            handleCloseUpdate();
        } else {
            toast.error('ไม่สำเร็จ');
            handleCloseUpdate();
        }
    };

    const onChangeStatus = (status) => {
        setNewStatus(status);
        if (status.transactionStatusKey === 12) {
            setShowModal(true);
        } else {
            setShowModalUpdate(true);
        }
    };

    useEffect(() => {
        if (forceChange != null) {
            const ns = statuses.find(x => x.transactionStatusKey === 10);
            if (ns) onChangeStatus(ns);
        }
    }, [forceChange]);

    useEffect(() => {
        const fetch = async () => {
            const { isSuccess, result } = await api.GetMasterTransactionStatus();
            if (isSuccess) {
                setStatuses((result?.items || []).sort((a, b) => a.transactionStatusKey - b.transactionStatusKey));
            }
        };
        fetch();
    }, []);

    const selectedStatus = statuses.find(s => s.transactionStatusKey === current);

    return (
        <div>
            <StatusDropdown
                selected={selectedStatus}
                statuses={statuses}
                current={current}
                onChange={onChangeStatus}
            />

            {/* ── Confirm Payment Modal ──────────────────────────────── */}
            <Modal show={showModal} onHide={handleClose} centered>
                <Modal.Header closeButton style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>ยืนยันการชำระเงิน</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        {file?.id && (
                            <div style={{ marginBottom: 12, textAlign: 'center' }}>
                                <ImageViewer id={file.id} />
                            </div>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label style={labelStyle}>อัปโหลดหลักฐานการชำระ</Form.Label>
                            <ImageUpload forceCrop={false} onUploadComplete={(f) => setFile(f)} />
                        </Form.Group>
                        <Form.Group className="mb-4">
                            <Form.Label style={labelStyle}>หมายเหตุ (ถ้ามี)</Form.Label>
                            <Form.Control
                                as="textarea" rows={3}
                                name="description"
                                value={paymentDetails.description}
                                onChange={(e) => setPaymentDetails(p => ({ ...p, description: e.target.value }))}
                                style={{ fontSize: 14 }}
                            />
                        </Form.Group>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <Button variant="light" onClick={handleClose} style={btnSecondary}>ยกเลิก</Button>
                            <Button variant="primary" type="submit" disabled={submitting || !file?.id} style={btnPrimary}>
                                {submitting ? <i className="fa fa-spinner fa-spin" /> : 'ยืนยัน'}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* ── Update Status Modal ────────────────────────────────── */}
            <Modal show={showModalUpdate} onHide={handleCloseUpdate} centered>
                <Modal.Header closeButton style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>เปลี่ยนสถานะ</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {/* Transition display */}
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        gap: 12, padding: '16px 20px', background: '#f9fafb',
                        borderRadius: 10, marginBottom: 20,
                    }}>
                        <StatusBadge status={selectedStatus} />
                        <i className="fa fa-arrow-right" style={{ color: '#9ca3af', fontSize: 14 }} />
                        <StatusBadge status={newStatus} />
                    </div>

                    <Form onSubmit={handleSubmitStatus}>
                        {newStatus?.transactionStatusKey === 10 && (
                            <Form.Group className="mb-3">
                                <Form.Label style={labelStyle}>ค่าจัดส่ง (บาท)</Form.Label>
                                <Form.Control
                                    type="number" name="shippingPrice" placeholder="0"
                                    value={paymentDetails.shippingPrice}
                                    onChange={(e) => setPaymentDetails(p => ({ ...p, shippingPrice: e.target.value }))}
                                    style={{ fontSize: 14 }}
                                />
                            </Form.Group>
                        )}
                        <Form.Group className="mb-4">
                            <Form.Label style={labelStyle}>
                                หมายเหตุ{newStatus?.isRequiredReason ? <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span> : ' (ถ้ามี)'}
                            </Form.Label>
                            <Form.Control
                                as="textarea" rows={3} name="description"
                                value={paymentDetails.description}
                                onChange={(e) => setPaymentDetails(p => ({ ...p, description: e.target.value }))}
                                isInvalid={!!errorMessage}
                                style={{ fontSize: 14 }}
                            />
                            <Form.Control.Feedback type="invalid">{errorMessage}</Form.Control.Feedback>
                        </Form.Group>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <Button variant="light" onClick={handleCloseUpdate} style={btnSecondary}>ยกเลิก</Button>
                            <Button
                                type="submit" disabled={submitting}
                                style={{ ...btnPrimary, background: newStatus?.color || '#1e3a5f', borderColor: newStatus?.color || '#1e3a5f' }}
                            >
                                {submitting ? <i className="fa fa-spinner fa-spin" /> : 'ยืนยัน'}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

const labelStyle = { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };
const btnPrimary = { fontSize: 14, fontWeight: 600, borderRadius: 8, padding: '7px 20px' };
const btnSecondary = { fontSize: 14, fontWeight: 500, borderRadius: 8, padding: '7px 16px', border: '1px solid #e5e7eb' };

export default TransactionStatusSelect;
